import { dateText } from '../../lib/time.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mailbox-forwarding ---
let __fez_component_0;
{
const MODES = [
  { value: 'keep', label: 'Keep it in the inbox' },
  { value: 'read', label: 'Keep it, marked read' },
  { value: 'archive', label: 'Archive it' },
]
__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: { type: String, required: true },
    can_edit: Boolean,
    on_change: { type: Function, state: true, default: () => {} },
  }

  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.items = []
    this.state.mode = 'keep'
    this.state.errors = {}
    this.state.busy = ''
    this.state.modes = MODES
  }

  onRefresh(props, changed) {
    if ('container' in changed || 'mailbox' in changed) this.load()
  }

  async load() {
    try {
      const data = await window.App.api(`container/${this.props.container}/forwards`, { mailbox: this.props.mailbox }, { silent: true })
      this.state.items = data.items
      this.state.mode = data.mode
      this.state.error = ''
    } catch (error) {
      this.state.error = error.message
    } finally {
      this.state.loading = false
    }
  }

  async add(event) {
    event.preventDefault()
    const form = event.target
    const address = (this.formData(form).address || '').trim()
    this.state.busy = 'add'
    try {
      await window.App.api(`container/${this.props.container}/forward_add`, { mailbox: this.props.mailbox, address })
      this.state.errors = {}
      form.reset()
      await this.changed()
    } catch (error) {
      this.state.errors = error.details || {}
    } finally {
      this.state.busy = ''
    }
  }

  async act(action, ref) {
    this.state.busy = ref
    try {
      await window.App.api(`container/${this.props.container}/${action}`, { mailbox: this.props.mailbox, forward: ref })
      await this.changed()
    } finally {
      this.state.busy = ''
    }
  }

  async setMode(event) {
    const mode = event.target.value
    await window.App.api(`container/${this.props.container}/forward_mode`, { mailbox: this.props.mailbox, mode }, { silent: true })
    this.state.mode = mode
    window.Toast.success('Saved')
    this.state.on_change()
  }

  async changed() {
    await this.load()
    this.state.on_change()
  }

  beforeRender() {
    this.state.rows = this.state.items.map((item) => ({
      ref: item.ref,
      address: item.address,
      badge: item.suppressed ? 'Bounced' : item.status === 'active' ? 'Forwarding' : 'Waiting for confirmation',
      tone: item.suppressed ? 'danger' : item.status === 'active' ? 'success' : 'warning',
      note: item.suppressed
        ? 'Mail to it bounced, so nothing more is sent there. An admin can lift that in the Sending settings.'
        : item.status === 'active'
          ? ''
          : `A confirmation link went to this address${item.expires_at ? `; it works until ${dateText(item.expires_at)}` : ''}. Nothing is forwarded until someone there confirms.`,
      pending: item.status !== 'active' && !item.suppressed,
      busy: this.state.busy === item.ref,
    }))
  }

  CSS = `:fez {
display:block;.fw-lead{margin:0 0 var(--space-4);color:var(--color-text-muted);font-size:var(--text-sm)}.fw-list{display:grid;gap:var(--space-2);margin-bottom:var(--space-4)}.fw-item{display:grid;gap:var(--space-2);padding:var(--space-3) var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-md)}.fw-head{display:flex;align-items:center;gap:var(--space-3)}.fw-address{flex:1;min-width:0;overflow:hidden;font-weight:var(--weight-semibold);text-overflow:ellipsis;white-space:nowrap}.fw-note{margin:0;color:var(--color-text-muted);font-size:var(--text-sm)}.fw-add{display:flex;align-items:flex-start;gap:var(--space-2)}.fw-add-field{flex:1;min-width:0}.fw-mode{margin-top:var(--space-4);max-width:320px}
}`

  HTML = `{#if state.error}
<ui-empty icon="warning-circle" title="Could not load the forwarding" text={state.error}></ui-empty>
{:else}
<p class="fw-lead">New mail of this mailbox is copied to these addresses as it arrives. Spam, delivery reports and mail forwarded to it are never sent on.</p>
{#if state.loading}
<ui-spinner></ui-spinner>
{:else}
{#if state.rows.length}
<div class="fw-list">
{#each state.rows as row}
<div class="fw-item" key={row.ref}>
<div class="fw-head">
<ui-icon name="arrow-bend-up-right" size="18"></ui-icon>
<span class="fw-address">{row.address}</span>
<ui-badge tone={row.tone} label={row.badge}></ui-badge>
{#if props.can_edit && row.pending}
<ui-button size="sm" variant="ghost" icon="paper-plane-tilt" label="Send again" loading={row.busy} onclick="fez.act('forward_resend', '{row.ref}')"></ui-button>
{/if}
{#if props.can_edit}
<ui-button size="sm" variant="ghost" icon="trash" title="Stop forwarding to it" onclick="fez.act('forward_remove', '{row.ref}')"></ui-button>
{/if}
</div>
{#if row.note}<p class="fw-note">{row.note}</p>{/if}
</div>
{/each}
</div>
{/if}
{#if props.can_edit}
<form class="fw-add" onsubmit="fez.add(event)" novalidate>
<span class="fw-add-field">
<ui-input name="address" type="email" placeholder="you@gmail.com" icon="arrow-bend-up-right" error={state.errors.address}></ui-input>
</span>
<ui-button type="submit" variant="primary" label="Forward" loading={state.busy === 'add'}></ui-button>
</form>
{:else if !state.rows.length}
<p class="fw-note">This mailbox forwards nothing.</p>
{/if}
{#if state.rows.length}
<div class="fw-mode" onchange="fez.setMode(event)">
<ui-select name="mode" label="Your copy" :options="state.modes" value={state.mode} disabled={!props.can_edit}></ui-select>
</div>
{/if}
{/if}
{/if}`
};
}

Fez('mailbox-forwarding', __fez_component_0);

export default __fez_component_0;
